import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '../../lib/utils'

const badgeVariants = cva(
  'tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-transparent tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:w-fit tw:whitespace-nowrap tw:shrink-0 tw:[&>svg]:size-3 tw:gap-1 tw:[&>svg]:pointer-events-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive tw:transition-[color,box-shadow] tw:overflow-hidden',
  {
    variants: {
      variant: {
        primary: 'tw:bg-primary tw:text-primary-foreground tw:[a&]:hover:bg-primary/90',
        default: 'tw:bg-primary/10 tw:text-primary tw:[a&]:hover:bg-primary/90',
        secondary: 'tw:bg-secondary tw:text-secondary-foreground tw:[a&]:hover:bg-secondary/90',
        light:  'tw:bg-secondary tw:text-secondary-foreground/60 tw:[a&]:hover:bg-secondary/90',
        destructive:
          'tw:bg-destructive tw:text-white tw:[a&]:hover:bg-destructive/90 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40 tw:dark:bg-destructive/60',
        'destructive-light': 'tw:bg-destructive/10 tw:[a&]:hover:bg-destructive/10 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40 tw:dark:bg-destructive/60',
        'emerald-light': 'tw:bg-emerald-50 tw:[a&]:hover:bg-emerald/10 tw:focus-visible:ring-emerald/20 tw:dark:focus-visible:ring-emerald/40 tw:dark:bg-emerald/60',
        outline: 'tw:border-border tw:text-foreground tw:[a&]:hover:bg-accent tw:[a&]:hover:text-accent-foreground',
        ghost: 'tw:[a&]:hover:bg-accent tw:[a&]:hover:text-accent-foreground',
        link: 'tw:text-primary tw:underline-offset-4 tw:[a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const Badge = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<'span'> &
    VariantProps<typeof badgeVariants> & { asChild?: boolean }
>(({ className, variant = 'default', asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      ref={ref}
      data-slot='badge'
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
})
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
